import { LoadingButton } from "@mui/lab";
import { Box, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { Plan } from "../../../models/accounts/plan";
import { Employee } from "../../../models/employees/employee";
import { SalarySacrifice } from "../../../models/salarySacrifices/salarySacrifice";
import { SideDrawer } from "../../shared/SideDrawer/SideDrawer";
import { useMutateSalarySacrifice } from "../../../hooks/salarySacrifices/useMutateSalarySacrifice";

interface SalarySacrificeAssignmentDrawerProps {
  salarySacrifice: SalarySacrifice;
  plans: Plan[];
  employees: Employee[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  refetch: () => void;
}

export const SalarySacrificeAssignmentDrawer: React.FC<
  SalarySacrificeAssignmentDrawerProps
> = ({ salarySacrifice, plans, employees, isOpen, setIsOpen, refetch }) => {

  const planSelectionOptions = plans.map((plan) => ({
    label: `${plan.name} - ${plan.alphanumericId}`,
    value: plan.alphanumericId
  }));

  const employeeSelectionOptions = employees.map((employee) => ({
    label: `${employee.firstName} ${employee.lastName} - ${employee.customerEmployeeId}`,
    value: employee.customerEmployeeId
  }));

  const [selectedPlanAlphanumericId, setSelectedPlanAlphanumericId] = useState<
    string | null
  >(
    plans.find((p) => p.alphanumericId === salarySacrifice.code)
      ?.alphanumericId ?? null
  );

  const [
    selectedEmployeeCustomerEmployeeId,
    setSelectedEmployeeCustomerEmployeeId
  ] = useState<string | null>(
    employees.find((e) => e.customerEmployeeId === salarySacrifice.reference)
      ?.customerEmployeeId ?? null
  );


  const mutateSalarySacrifice = useMutateSalarySacrifice({
    onSuccess: () => {
      refetch();
      setIsOpen(false);
    },
  });

  return (
    <SideDrawer title="Assign Contribution" open={isOpen} setIsOpen={setIsOpen}>
      <Stack spacing={3} mx={2}>
        <Box>
          <Typography variant="subtitle2" color="black" fontWeight="bold">
            Account
          </Typography>
          <Select
            value={selectedPlanAlphanumericId}
            onChange={(e) => setSelectedPlanAlphanumericId(e.target.value)}
            fullWidth
            required
            size="small"
          >
            {planSelectionOptions.map((pso) => (
              <MenuItem value={pso.value} key={pso.value}>
                {pso.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="black" fontWeight="bold">
            Employee
          </Typography>
          <Select
            value={selectedEmployeeCustomerEmployeeId}
            onChange={(e) =>
              setSelectedEmployeeCustomerEmployeeId(e.target.value)
            }
            fullWidth
            required
            size="small"
          >
            {employeeSelectionOptions.map((eso) => (
              <MenuItem value={eso.value} key={eso.value}>
                {eso.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <LoadingButton
          variant="contained"
          color="primary"
          onClick={() => mutateSalarySacrifice.mutate({
            salarySacrificeId: salarySacrifice.id,
            code: selectedPlanAlphanumericId!,
            reference: selectedEmployeeCustomerEmployeeId!
          })}
          loading={mutateSalarySacrifice.isLoading}
          disabled={
            !selectedPlanAlphanumericId || !selectedEmployeeCustomerEmployeeId
          }
        >
          Update
        </LoadingButton>
      </Stack>
    </SideDrawer>
  );
};
